import { reactive } from "vue";

export type PropertyType = "text" | "number" | "date" | "boolean";
export type PlannedStatus =
  | "planned"
  | "in-progress"
  | "completed"
  | "cancelled";
export type DeploymentStatus = "completed" | "in-progress" | "not-started";
export type NoticeKind = "success" | "error" | "info";

export interface User {
  id: string;
  name: string;
  surname: string;
  email: string;
}

export interface DeployUnit {
  id: string;
  name: string;
  description: string;
  createdAt: string;
}

export interface ApplicationProperty {
  id: string;
  name: string;
  type: PropertyType;
  createdAt: string;
}

export interface Application {
  id: string;
  name: string;
  propertyValues: Record<string, string | number | boolean | null>;
  createdAt: string;
  updatedAt: string;
}

export interface Section {
  id: string;
  name: string;
  deployUnitId: string;
  position: number;
}

export interface Step {
  id: string;
  name: string;
  description: string;
  sectionId: string;
  position: number;
}

export interface Ring {
  id: string;
  name: string;
  description: string;
  order: number;
  color: string;
  createdAt: string;
}

export interface Customer {
  id: string;
  name: string;
  currentVersion: string;
  lastDeployDate: string;
  deployedBy: string;
  createdAt: string;
  ringId: string | null;
  deployUnitIds: string[];
  liveDate: string;
  dbValidDate: string;
}

export interface FutureDeployNote {
  id: string;
  title: string;
  content: string;
  customerName: string;
  targetVersion: string;
  deployUnitId: string;
  createdAt: string;
}

export interface PlannedDeploy {
  id: string;
  customerName: string;
  currentVersion: string;
  targetVersion: string;
  plannedDate: string;
  assignedUserId: string;
  notes: string;
  status: PlannedStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Branch {
  id: string;
  name: string;
  startingIteration: number;
  prodIteration: number;
  devIteration: number;
  createdAt: string;
}

export interface DeploymentStepState {
  stepId: string;
  completed: boolean;
  notes: string;
}

export interface Deployment {
  id: string;
  customerName: string;
  currentVersion: string;
  targetVersion: string;
  userId: string;
  branchId: string | null;
  createdAt: string;
  steps: DeploymentStepState[];
}

export interface BusinessData {
  users: User[];
  deployUnits: DeployUnit[];
  applicationProperties: ApplicationProperty[];
  applications: Application[];
  sections: Section[];
  steps: Step[];
  rings: Ring[];
  customers: Customer[];
  futureDeployNotes: FutureDeployNote[];
  plannedDeploys: PlannedDeploy[];
  branches: Branch[];
  deployments: Deployment[];
}

export interface Notice {
  id: string;
  kind: NoticeKind;
  text: string;
}

export interface PlannedHandoff {
  customerName: string;
  currentVersion: string;
  targetVersion: string;
  userId: string;
}

export interface UiState {
  activeDeploymentId: string | null;
  selectedHistoryDeploymentId: string | null;
  plannedHandoff: PlannedHandoff | null;
  notice: Notice | null;
}

export interface DeploymentProgress {
  completed: number;
  total: number;
  percentage: number;
  status: DeploymentStatus;
}

export interface GroupedDeploymentStep {
  state: DeploymentStepState;
  stepName: string;
  stepDescription: string;
  unitName: string;
  sectionName: string;
}

export interface DeploymentUnitGroup {
  key: string;
  unitName: string;
  sections: Array<{
    key: string;
    sectionName: string;
    steps: GroupedDeploymentStep[];
  }>;
}

const STORAGE_KEY = "customer-manager-data-v1";
const LEGACY_KEYS = ["customer-manager-categories"];

let sequence = 0;

function nowIso(): string {
  return new Date().toISOString();
}

function today(): string {
  return nowIso().slice(0, 10);
}

function createId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${Date.now()}-${sequence}`;
}

function compareVersions(left: string, right: string): number {
  return right.localeCompare(left, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function parseIteration(targetVersion: string): number | null {
  const match = targetVersion.match(/(\d+)\s*$/);
  return match ? Number(match[1]) : null;
}

function emptyData(): BusinessData {
  return {
    users: [],
    deployUnits: [],
    applicationProperties: [],
    applications: [],
    sections: [],
    steps: [],
    rings: [],
    customers: [],
    futureDeployNotes: [],
    plannedDeploys: [],
    branches: [],
    deployments: [],
  };
}

function normalizeData(
  value: Partial<BusinessData> | null | undefined
): BusinessData {
  const data = value ?? {};
  return {
    users: Array.isArray(data.users) ? data.users : [],
    deployUnits: Array.isArray(data.deployUnits) ? data.deployUnits : [],
    applicationProperties: Array.isArray(data.applicationProperties)
      ? data.applicationProperties
      : [],
    applications: Array.isArray(data.applications) ? data.applications : [],
    sections: Array.isArray(data.sections) ? data.sections : [],
    steps: Array.isArray(data.steps) ? data.steps : [],
    rings: Array.isArray(data.rings) ? data.rings : [],
    customers: Array.isArray(data.customers) ? data.customers : [],
    futureDeployNotes: Array.isArray(data.futureDeployNotes)
      ? data.futureDeployNotes
      : [],
    plannedDeploys: Array.isArray(data.plannedDeploys)
      ? data.plannedDeploys
      : [],
    branches: Array.isArray(data.branches) ? data.branches : [],
    deployments: Array.isArray(data.deployments) ? data.deployments : [],
  };
}

function loadData(): BusinessData {
  if (typeof window === "undefined") {
    return emptyData();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return emptyData();
    }
    return normalizeData(JSON.parse(raw) as Partial<BusinessData>);
  } catch (error) {
    return emptyData();
  }
}

function clonePropertyValues(
  properties: ApplicationProperty[],
  source?: Record<string, string | number | boolean | null>
): Record<string, string | number | boolean | null> {
  return properties.reduce<Record<string, string | number | boolean | null>>(
    (accumulator, property) => {
      accumulator[property.id] =
        source && property.id in source ? source[property.id] : null;
      return accumulator;
    },
    {}
  );
}

function sortByDateAscendingWithUndatedLast<T extends { plannedDate: string }>(
  items: T[]
): T[] {
  return [...items].sort((left, right) => {
    if (!left.plannedDate && !right.plannedDate) {
      return 0;
    }
    if (!left.plannedDate) {
      return 1;
    }
    if (!right.plannedDate) {
      return -1;
    }
    return left.plannedDate.localeCompare(right.plannedDate);
  });
}

function sortSections(sections: Section[], deployUnitId: string): Section[] {
  return sections
    .filter((section) => section.deployUnitId === deployUnitId)
    .sort((left, right) => left.position - right.position);
}

function sortSteps(steps: Step[], sectionId: string): Step[] {
  return steps
    .filter((step) => step.sectionId === sectionId)
    .sort((left, right) => left.position - right.position);
}

function resequenceSections(data: BusinessData, deployUnitId: string): void {
  sortSections(data.sections, deployUnitId).forEach((section, index) => {
    section.position = index + 1;
  });
}

function resequenceSteps(data: BusinessData, sectionId: string): void {
  sortSteps(data.steps, sectionId).forEach((step, index) => {
    step.position = index + 1;
  });
}

function resequenceRings(data: BusinessData): void {
  [...data.rings]
    .sort((left, right) => left.order - right.order)
    .forEach((ring, index) => {
      ring.order = index + 1;
    });
}

function getProgress(deployment: Deployment): DeploymentProgress {
  const completed = deployment.steps.filter((step) => step.completed).length;
  const total = deployment.steps.length;
  const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);
  let status: DeploymentStatus = "in-progress";

  if (completed === total && total > 0) {
    status = "completed";
  } else if (completed === 0) {
    status = "not-started";
  }

  return { completed, total, percentage, status };
}

function recalculateBranches(data: BusinessData): void {
  data.branches.forEach((branch) => {
    const referencedDeployments = data.deployments.filter(
      (deployment) => deployment.branchId === branch.id
    );
    const iterations = referencedDeployments
      .map((deployment) => parseIteration(deployment.targetVersion))
      .filter((value): value is number => value !== null);
    const prodIteration = iterations.length
      ? Math.max(...iterations)
      : branch.startingIteration;

    branch.prodIteration = prodIteration;
    branch.devIteration = prodIteration + 1;
  });
}

function persistData(data: BusinessData): void {
  if (typeof window === "undefined") {
    return;
  }

  recalculateBranches(data);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function clearLegacyKeys(): void {
  if (typeof window === "undefined") {
    return;
  }

  LEGACY_KEYS.forEach((key) => window.localStorage.removeItem(key));
}

function normalizeApplicationValue(
  type: PropertyType,
  rawValue: string | number | boolean | null
): string | number | boolean | null {
  if (rawValue === null || rawValue === "") {
    return null;
  }

  if (type === "number") {
    const numericValue =
      typeof rawValue === "number" ? rawValue : Number(String(rawValue));
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  if (type === "boolean") {
    return Boolean(rawValue);
  }

  return String(rawValue);
}

function downloadText(
  filename: string,
  content: string,
  mimeType: string
): void {
  if (typeof window === "undefined") {
    return;
  }

  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function renderPrintableWindow(title: string, body: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const popup = window.open("", "_blank", "width=1100,height=800");
  if (!popup) {
    return;
  }

  popup.document.write(`<!doctype html>
<html>
  <head>
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #18212f; }
      h1, h2, h3 { margin-bottom: 0.5rem; }
      .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; margin-bottom: 20px; }
      .card { border: 1px solid #d6dde8; border-radius: 10px; padding: 16px; margin-bottom: 16px; }
      .step { padding: 8px 0; border-bottom: 1px solid #edf2f7; }
      .step:last-child { border-bottom: none; }
      .note { white-space: pre-wrap; color: #46556b; }
      @media print { button { display: none; } body { padding: 0; } }
    </style>
  </head>
  <body>
    ${body}
    <script>window.onload = function () { window.print(); };</script>
  </body>
</html>`);
  popup.document.close();
}

function createDeploymentCsvRows(
  deployment: Deployment,
  groups: DeploymentUnitGroup[],
  userLabel: string
): string[][] {
  const progress = getProgress(deployment);
  const rows: string[][] = [];
  const baseColumns = [
    deployment.id,
    deployment.customerName,
    deployment.currentVersion,
    deployment.targetVersion,
    userLabel,
    deployment.createdAt,
    String(progress.total),
    String(progress.completed),
    String(progress.percentage),
  ];

  const stepRows = groups.flatMap((unit) =>
    unit.sections.flatMap((section) =>
      section.steps.map((step) => [
        step.stepName,
        unit.unitName,
        step.state.completed ? "Yes" : "No",
        step.state.notes,
      ])
    )
  );

  if (!stepRows.length) {
    rows.push([...baseColumns, "", "", "", ""]);
    return rows;
  }

  stepRows.forEach((stepRow, index) => {
    rows.push(
      index === 0
        ? [...baseColumns, ...stepRow]
        : ["", "", "", "", "", "", "", "", "", ...stepRow]
    );
  });

  return rows;
}

function toCsv(rows: string[][]): string {
  return rows
    .map((row) =>
      row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");
}

function seedData(size: number): BusinessData {
  if (size <= 0) {
    return emptyData();
  }

  const data = emptyData();
  const createdAt = nowIso();
  const firstNames = [
    "Ava",
    "Luca",
    "Mia",
    "Noah",
    "Emma",
    "Leo",
    "Nora",
    "Owen",
    "Sofia",
    "Milo",
  ];
  const surnames = [
    "Stone",
    "Reed",
    "Bennett",
    "Turner",
    "Rossi",
    "Khan",
    "Morris",
    "Patel",
    "Lopez",
    "Hayes",
  ];
  const unitNames = ["Payments", "Billing", "Identity", "Reporting", "Portal"];
  const ringColors = ["#0f766e", "#2563eb", "#d97706", "#7c3aed", "#dc2626"];
  const customerPrefixes = [
    "Northwind",
    "Contoso",
    "Fabrikam",
    "Adventure",
    "Bluebird",
    "Redwood",
    "Summit",
    "Helios",
    "Meridian",
    "Harbor",
  ];
  const branchNames = ["Release", "Hotfix", "Pilot", "Canary"];

  for (let index = 0; index < size; index += 1) {
    data.users.push({
      id: createId("user"),
      name: firstNames[index % firstNames.length],
      surname: surnames[index % surnames.length],
      email: `${firstNames[index % firstNames.length].toLowerCase()}.${surnames[
        index % surnames.length
      ].toLowerCase()}@example.com`,
    });
  }

  ["Environment", "Priority", "Release Date", "Customer Facing"].forEach(
    (name, index) => {
      const type = (["text", "number", "date", "boolean"] as PropertyType[])[
        index
      ];
      data.applicationProperties.push({
        id: createId("property"),
        name,
        type,
        createdAt,
      });
    }
  );

  for (let index = 0; index < Math.max(3, Math.min(5, size)); index += 1) {
    const deployUnitId = createId("unit");
    data.deployUnits.push({
      id: deployUnitId,
      name: unitNames[index % unitNames.length],
      description: `${unitNames[index % unitNames.length]} deployment scope`,
      createdAt,
    });

    for (let sectionIndex = 0; sectionIndex < 2; sectionIndex += 1) {
      const sectionId = createId("section");
      data.sections.push({
        id: sectionId,
        name: sectionIndex === 0 ? "Preparation" : "Validation",
        deployUnitId,
        position: sectionIndex + 1,
      });

      for (let stepIndex = 0; stepIndex < 3; stepIndex += 1) {
        data.steps.push({
          id: createId("step"),
          name:
            sectionIndex === 0
              ? `Prep task ${stepIndex + 1}`
              : `Validation task ${stepIndex + 1}`,
          description: `${unitNames[index % unitNames.length]} step ${
            stepIndex + 1
          }`,
          sectionId,
          position: stepIndex + 1,
        });
      }
    }
  }

  for (let index = 0; index < Math.max(3, Math.min(4, size)); index += 1) {
    data.rings.push({
      id: createId("ring"),
      name: `Ring ${index + 1}`,
      description: index === 0 ? "Early rollout" : `Wave ${index + 1}`,
      order: index + 1,
      color: ringColors[index % ringColors.length],
      createdAt,
    });
  }

  for (let index = 0; index < size; index += 1) {
    const customerName = `${
      customerPrefixes[index % customerPrefixes.length]
    } ${index + 1}`;
    const assignedUnitIds = data.deployUnits
      .filter((_, unitIndex) => (unitIndex + index) % 2 === 0)
      .slice(0, index % 3 === 0 ? 1 : 2)
      .map((unit) => unit.id);
    const ringId =
      index === size - 1 && size > 5
        ? null
        : data.rings[index % data.rings.length]?.id ?? null;
    const user = data.users[index % data.users.length];

    data.customers.push({
      id: createId("customer"),
      name: customerName,
      currentVersion: `release-2026.${(index % 4) + 1}.${index + 10}`,
      lastDeployDate:
        index % 3 === 0
          ? ""
          : new Date(Date.now() - index * 12 * 24 * 60 * 60 * 1000)
              .toISOString()
              .slice(0, 10),
      deployedBy: `${user.name} ${user.surname}`,
      createdAt,
      ringId,
      deployUnitIds: assignedUnitIds,
      liveDate: index % 2 === 0 ? today() : "",
      dbValidDate: index % 4 === 0 ? today() : "",
    });
  }

  for (let index = 0; index < size; index += 1) {
    const propertyValues = clonePropertyValues(data.applicationProperties);
    const appId = createId("application");
    data.applicationProperties.forEach((property, propertyIndex) => {
      const values: Array<string | number | boolean> = [
        unitNames[index % unitNames.length],
        index + 1,
        today(),
        index % 2 === 0,
      ];
      propertyValues[property.id] = values[propertyIndex] ?? null;
    });

    data.applications.push({
      id: appId,
      name: `Application ${index + 1}`,
      propertyValues,
      createdAt,
      updatedAt: createdAt,
    });
  }

  data.futureDeployNotes.push(
    {
      id: createId("note"),
      title: "Global pre-check",
      content:
        "Confirm monitoring, communication, and rollback contacts before every deployment.",
      customerName: "",
      targetVersion: "",
      deployUnitId: "",
      createdAt,
    },
    {
      id: createId("note"),
      title: "Northwind billing reminder",
      content: "Billing deployments for Northwind require finance sign-off.",
      customerName: data.customers[0]?.name ?? "",
      targetVersion: "",
      deployUnitId: data.deployUnits[1]?.id ?? "",
      createdAt,
    },
    {
      id: createId("note"),
      title: "Version-specific script",
      content: "Apply the 2026.3 data patch before validation.",
      customerName: "",
      targetVersion: "release-2026.3.15",
      deployUnitId: "",
      createdAt,
    },
    {
      id: createId("note"),
      title: "Identity warm-up",
      content: "Keep authentication caches warm for 10 minutes after rollout.",
      customerName: "",
      targetVersion: "",
      deployUnitId: data.deployUnits[2]?.id ?? "",
      createdAt,
    }
  );

  for (let index = 0; index < Math.max(3, size - 1); index += 1) {
    const customer = data.customers[index % data.customers.length];
    data.plannedDeploys.push({
      id: createId("planned"),
      customerName: customer.name,
      currentVersion: customer.currentVersion,
      targetVersion: `release-2026.${(index % 4) + 2}.${index + 20}`,
      plannedDate:
        index === 0
          ? today()
          : new Date(Date.now() + index * 3 * 24 * 60 * 60 * 1000)
              .toISOString()
              .slice(0, 10),
      assignedUserId: data.users[index % data.users.length]?.id ?? "",
      notes: index % 2 === 0 ? "Coordinate with support before rollout." : "",
      status: (
        ["planned", "completed", "cancelled", "in-progress"] as PlannedStatus[]
      )[index % 4],
      createdAt,
      updatedAt: createdAt,
    });
  }

  for (let index = 0; index < Math.max(2, Math.min(4, size)); index += 1) {
    data.branches.push({
      id: createId("branch"),
      name: branchNames[index % branchNames.length],
      startingIteration: 10 + index,
      prodIteration: 10 + index,
      devIteration: 11 + index,
      createdAt,
    });
  }

  const deploymentTargets = [
    ...data.customers.slice(0, Math.max(3, Math.min(size, 6))),
    {
      id: "historic-only",
      name: "Legacy Retail",
      currentVersion: "release-2025.4.8",
      deployUnitIds: data.deployUnits.slice(0, 1).map((unit) => unit.id),
    },
  ];

  deploymentTargets.forEach((target, index) => {
    const branch = data.branches[index % data.branches.length] ?? null;
    const user = data.users[index % data.users.length];
    const targetUnitIds = target.deployUnitIds ?? [];
    const targetSectionIds = data.sections
      .filter((section) => targetUnitIds.includes(section.deployUnitId))
      .map((section) => section.id);
    const states = data.steps
      .filter((step) => targetSectionIds.includes(step.sectionId))
      .sort((left, right) => {
        const leftSection = data.sections.find(
          (section) => section.id === left.sectionId
        );
        const rightSection = data.sections.find(
          (section) => section.id === right.sectionId
        );
        return (
          (leftSection?.position ?? 0) - (rightSection?.position ?? 0) ||
          left.position - right.position
        );
      })
      .map((step, stepIndex) => ({
        stepId: step.id,
        completed: index % 3 === 0 ? true : stepIndex < (index % 3) + 1,
        notes:
          stepIndex === 0 ? "Deployment note captured during execution." : "",
      }));

    if (index === deploymentTargets.length - 1) {
      states.length = 0;
    }

    data.deployments.push({
      id: createId("deploy"),
      customerName: target.name,
      currentVersion: target.currentVersion,
      targetVersion: `release-2026.${(index % 4) + 2}.${20 + index}`,
      userId: user.id,
      branchId: branch?.id ?? null,
      createdAt: new Date(
        Date.now() - index * 5 * 24 * 60 * 60 * 1000
      ).toISOString(),
      steps: states,
    });
  });

  recalculateBranches(data);
  return data;
}

const state = reactive<{
  data: BusinessData;
  ui: UiState;
}>({
  data: loadData(),
  ui: {
    activeDeploymentId: null,
    selectedHistoryDeploymentId: null,
    plannedHandoff: null,
    notice: null,
  },
});

recalculateBranches(state.data);

function mutate<T>(callback: () => T): T {
  const result = callback();
  persistData(state.data);
  return result;
}

function resetUiState(): void {
  state.ui.activeDeploymentId = null;
  state.ui.selectedHistoryDeploymentId = null;
  state.ui.plannedHandoff = null;
}

function setNotice(text: string, kind: NoticeKind = "success"): void {
  state.ui.notice = {
    id: createId("notice"),
    text,
    kind,
  };
}

function syncCustomerOnCompletedDeployment(deployment: Deployment): void {
  const progress = getProgress(deployment);
  if (progress.status !== "completed") {
    return;
  }

  const customer = state.data.customers.find(
    (candidate) => candidate.name === deployment.customerName
  );
  if (!customer) {
    return;
  }

  customer.currentVersion = deployment.targetVersion;
  customer.lastDeployDate = deployment.createdAt.slice(0, 10);
  customer.deployedBy = getUserLabel(deployment.userId);
}

export function getUserLabel(userId: string): string {
  const user = state.data.users.find((candidate) => candidate.id === userId);
  return user ? `${user.name} ${user.surname}` : "Deleted user";
}

export function getRingLabel(ringId: string | null): string {
  if (!ringId) {
    return "Unassigned";
  }
  return (
    state.data.rings.find((ring) => ring.id === ringId)?.name ?? "Missing ring"
  );
}

export function getDeployUnitLabel(deployUnitId: string): string {
  return (
    state.data.deployUnits.find((unit) => unit.id === deployUnitId)?.name ??
    "Missing deploy unit"
  );
}

export function getMatchingNotes(context: {
  customerName: string;
  targetVersion: string;
  deployUnitIds: string[];
}): FutureDeployNote[] {
  return state.data.futureDeployNotes.filter((note) => {
    if (note.customerName && note.customerName !== context.customerName) {
      return false;
    }
    if (note.targetVersion && note.targetVersion !== context.targetVersion) {
      return false;
    }
    if (
      note.deployUnitId &&
      !context.deployUnitIds.includes(note.deployUnitId)
    ) {
      return false;
    }
    return true;
  });
}

export function getDeploymentGroups(
  deployment: Deployment
): DeploymentUnitGroup[] {
  const groups: DeploymentUnitGroup[] = [];

  deployment.steps.forEach((stateRecord) => {
    const step = state.data.steps.find(
      (candidate) => candidate.id === stateRecord.stepId
    );
    const section = step
      ? state.data.sections.find((candidate) => candidate.id === step.sectionId)
      : null;
    const unit = section
      ? state.data.deployUnits.find(
          (candidate) => candidate.id === section.deployUnitId
        )
      : null;
    const unitName = unit?.name ?? "Archived deploy unit";
    const sectionName = section?.name ?? "Archived section";
    const unitKey = unit?.id ?? `archived-unit-${sectionName}`;
    const sectionKey = section?.id ?? `archived-section-${stateRecord.stepId}`;

    let unitGroup = groups.find((candidate) => candidate.key === unitKey);
    if (!unitGroup) {
      unitGroup = {
        key: unitKey,
        unitName,
        sections: [],
      };
      groups.push(unitGroup);
    }

    let sectionGroup = unitGroup.sections.find(
      (candidate) => candidate.key === sectionKey
    );
    if (!sectionGroup) {
      sectionGroup = {
        key: sectionKey,
        sectionName,
        steps: [],
      };
      unitGroup.sections.push(sectionGroup);
    }

    sectionGroup.steps.push({
      state: stateRecord,
      stepName: step?.name ?? "Archived step",
      stepDescription: step?.description ?? "",
      unitName,
      sectionName,
    });
  });

  return groups;
}

export function getCustomerOverview(search: string): Array<{
  key: string;
  customer: Customer | null;
  customerName: string;
  ringName: string;
  deployUnits: string[];
  currentVersion: string;
  lastDeployDate: string;
  liveDate: string;
  dbValidDate: string;
  deployedBy: string;
  totalDeployCount: number;
  latestDeployStatus: string;
  latestDeploymentId: string | null;
}> {
  const names = new Set<string>();
  state.data.customers.forEach((customer) => names.add(customer.name));
  state.data.deployments.forEach((deployment) =>
    names.add(deployment.customerName)
  );

  const normalizedSearch = search.trim().toLowerCase();

  return Array.from(names)
    .map((customerName) => {
      const customer =
        state.data.customers.find(
          (candidate) => candidate.name === customerName
        ) ?? null;
      const deployments = state.data.deployments
        .filter((deployment) => deployment.customerName === customerName)
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      const latestDeployment = deployments[0] ?? null;
      const progress = latestDeployment ? getProgress(latestDeployment) : null;
      const ringName = customer
        ? getRingLabel(customer.ringId)
        : "Historical only";
      const deployUnits = customer
        ? customer.deployUnitIds.map((deployUnitId) =>
            getDeployUnitLabel(deployUnitId)
          )
        : [];
      const currentVersion =
        customer?.currentVersion ?? latestDeployment?.targetVersion ?? "";
      const lastDeployDate =
        customer?.lastDeployDate ??
        latestDeployment?.createdAt.slice(0, 10) ??
        "";
      const deployedBy =
        customer?.deployedBy ??
        (latestDeployment ? getUserLabel(latestDeployment.userId) : "");
      const latestDeployStatus = progress
        ? progress.status === "completed"
          ? "Completed"
          : progress.status === "in-progress"
          ? "In Progress"
          : "Not Started"
        : "No Deploy";

      return {
        key: customer?.id ?? `history-${customerName}`,
        customer,
        customerName,
        ringName,
        deployUnits,
        currentVersion,
        lastDeployDate,
        liveDate: customer?.liveDate ?? "",
        dbValidDate: customer?.dbValidDate ?? "",
        deployedBy,
        totalDeployCount: deployments.length,
        latestDeployStatus,
        latestDeploymentId: latestDeployment?.id ?? null,
      };
    })
    .filter((row) => {
      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        row.customerName,
        row.currentVersion,
        row.deployedBy,
        row.ringName,
      ]
        .join(" ")
        .toLowerCase();
      return searchBlob.includes(normalizedSearch);
    })
    .sort((left, right) => left.customerName.localeCompare(right.customerName));
}

export function getHistoryRows(
  search: string,
  statusFilter: string
): Deployment[] {
  const normalizedSearch = search.trim().toLowerCase();
  return [...state.data.deployments]
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .filter((deployment) => {
      const userLabel = getUserLabel(deployment.userId);
      const progress = getProgress(deployment);
      const matchesSearch = normalizedSearch
        ? [
            deployment.customerName,
            deployment.currentVersion,
            deployment.targetVersion,
            userLabel,
          ]
            .join(" ")
            .toLowerCase()
            .includes(normalizedSearch)
        : true;
      const matchesStatus =
        statusFilter === "all" ? true : progress.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
}

export function useCustomerManager(): {
  state: typeof state;
  getProgress: typeof getProgress;
  getDeploymentGroups: typeof getDeploymentGroups;
  getMatchingNotes: typeof getMatchingNotes;
  getCustomerOverview: typeof getCustomerOverview;
  getHistoryRows: typeof getHistoryRows;
  getUserLabel: typeof getUserLabel;
  getRingLabel: typeof getRingLabel;
  getDeployUnitLabel: typeof getDeployUnitLabel;
  compareVersions: typeof compareVersions;
  sortByDateAscendingWithUndatedLast: typeof sortByDateAscendingWithUndatedLast;
  clearNotice: () => void;
  clearPlannedHandoff: () => void;
  setHistorySelection: (deploymentId: string | null) => void;
  setActiveDeployment: (deploymentId: string | null) => void;
  createUser: (payload: Omit<User, "id">) => void;
  updateUser: (id: string, payload: Omit<User, "id">) => void;
  deleteUser: (id: string) => boolean;
  createDeployUnit: (payload: Omit<DeployUnit, "id" | "createdAt">) => void;
  updateDeployUnit: (
    id: string,
    payload: Omit<DeployUnit, "id" | "createdAt">
  ) => void;
  deleteDeployUnit: (id: string) => void;
  createSection: (payload: {
    name: string;
    deployUnitId: string;
    position: number;
  }) => void;
  updateSection: (
    id: string,
    payload: { name: string; deployUnitId: string; position: number }
  ) => void;
  deleteSection: (id: string) => boolean;
  createStep: (payload: {
    name: string;
    description: string;
    sectionId: string;
    position: number;
  }) => void;
  updateStep: (
    id: string,
    payload: {
      name: string;
      description: string;
      sectionId: string;
      position: number;
    }
  ) => void;
  deleteStep: (id: string) => void;
  createRing: (payload: Omit<Ring, "id" | "createdAt" | "order">) => void;
  updateRing: (id: string, payload: Omit<Ring, "id" | "createdAt">) => void;
  deleteRing: (id: string) => boolean;
  moveRing: (id: string, direction: number) => void;
  assignCustomersToRing: (ringId: string, customerIds: string[]) => void;
  removeCustomerFromRing: (customerId: string) => void;
  createCustomer: (payload: Omit<Customer, "id" | "createdAt">) => void;
  updateCustomer: (
    id: string,
    payload: Omit<Customer, "id" | "createdAt">
  ) => void;
  deleteCustomer: (id: string) => void;
  createNote: (payload: Omit<FutureDeployNote, "id" | "createdAt">) => void;
  updateNote: (
    id: string,
    payload: Omit<FutureDeployNote, "id" | "createdAt">
  ) => void;
  deleteNote: (id: string) => void;
  createPlannedDeploy: (
    payload: Omit<PlannedDeploy, "id" | "createdAt" | "updatedAt">
  ) => void;
  updatePlannedDeploy: (
    id: string,
    payload: Omit<PlannedDeploy, "id" | "createdAt" | "updatedAt">
  ) => void;
  deletePlannedDeploy: (id: string) => void;
  setPlannedDeployStatus: (id: string, status: PlannedStatus) => void;
  startPlannedDeploy: (id: string) => boolean;
  createBranch: (payload: { name: string; startingIteration: number }) => void;
  updateBranch: (
    id: string,
    payload: { name: string; startingIteration: number }
  ) => void;
  deleteBranch: (id: string) => boolean;
  createApplication: (name: string) => void;
  renameApplication: (id: string, name: string) => void;
  deleteApplication: (id: string) => void;
  setApplicationValue: (
    applicationId: string,
    propertyId: string,
    value: string | number | boolean | null
  ) => void;
  createApplicationProperty: (payload: {
    name: string;
    type: PropertyType;
  }) => void;
  updateApplicationProperty: (
    id: string,
    payload: { name: string; type: PropertyType }
  ) => void;
  deleteApplicationProperty: (id: string) => void;
  startDeployment: (payload: {
    customerId: string;
    currentVersion: string;
    targetVersion: string;
    userId: string;
    branchId: string;
  }) => boolean;
  toggleDeploymentStep: (
    deploymentId: string,
    stepId: string,
    completed: boolean
  ) => void;
  updateDeploymentStepNotes: (
    deploymentId: string,
    stepId: string,
    notes: string
  ) => void;
  deleteAllData: () => void;
  generateDemoData: (size: number) => void;
  exportDeploymentCsv: (deploymentId: string) => void;
  exportFilteredDeploymentsCsv: (deployments: Deployment[]) => void;
  printDeployment: (deploymentId: string) => void;
  sendReminder: (message: string, recipients: string[]) => void;
  loadDocs: () => Promise<string>;
} {
  return {
    state,
    getProgress,
    getDeploymentGroups,
    getMatchingNotes,
    getCustomerOverview,
    getHistoryRows,
    getUserLabel,
    getRingLabel,
    getDeployUnitLabel,
    compareVersions,
    sortByDateAscendingWithUndatedLast,
    clearNotice: () => {
      state.ui.notice = null;
    },
    clearPlannedHandoff: () => {
      state.ui.plannedHandoff = null;
    },
    setHistorySelection: (deploymentId: string | null) => {
      state.ui.selectedHistoryDeploymentId = deploymentId;
    },
    setActiveDeployment: (deploymentId: string | null) => {
      state.ui.activeDeploymentId = deploymentId;
    },
    createUser: (payload) => {
      mutate(() => {
        state.data.users.push({ id: createId("user"), ...payload });
      });
      setNotice("User saved.");
    },
    updateUser: (id, payload) => {
      mutate(() => {
        const user = state.data.users.find((candidate) => candidate.id === id);
        if (user) {
          Object.assign(user, payload);
        }
      });
      setNotice("User updated.");
    },
    deleteUser: (id) => {
      mutate(() => {
        state.data.users = state.data.users.filter((user) => user.id !== id);
      });
      setNotice("User deleted.");
      return true;
    },
    createDeployUnit: (payload) => {
      mutate(() => {
        state.data.deployUnits.push({
          id: createId("unit"),
          createdAt: nowIso(),
          ...payload,
        });
      });
      setNotice("Deploy unit saved.");
    },
    updateDeployUnit: (id, payload) => {
      mutate(() => {
        const deployUnit = state.data.deployUnits.find(
          (candidate) => candidate.id === id
        );
        if (deployUnit) {
          Object.assign(deployUnit, payload);
        }
      });
      setNotice("Deploy unit updated.");
    },
    deleteDeployUnit: (id) => {
      mutate(() => {
        const sectionIds = state.data.sections
          .filter((section) => section.deployUnitId === id)
          .map((section) => section.id);
        state.data.deployUnits = state.data.deployUnits.filter(
          (deployUnit) => deployUnit.id !== id
        );
        state.data.sections = state.data.sections.filter(
          (section) => section.deployUnitId !== id
        );
        state.data.steps = state.data.steps.filter(
          (step) => !sectionIds.includes(step.sectionId)
        );
        state.data.customers.forEach((customer) => {
          customer.deployUnitIds = customer.deployUnitIds.filter(
            (deployUnitId) => deployUnitId !== id
          );
        });
      });
      setNotice("Deploy unit deleted.");
    },
    createSection: (payload) => {
      mutate(() => {
        const siblings = sortSections(
          state.data.sections,
          payload.deployUnitId
        );
        const targetPosition = Math.min(
          Math.max(1, payload.position || siblings.length + 1),
          siblings.length + 1
        );
        siblings.forEach((section) => {
          if (section.position >= targetPosition) {
            section.position += 1;
          }
        });
        state.data.sections.push({
          id: createId("section"),
          name: payload.name,
          deployUnitId: payload.deployUnitId,
          position: targetPosition,
        });
        resequenceSections(state.data, payload.deployUnitId);
      });
      setNotice("Section saved.");
    },
    updateSection: (id, payload) => {
      mutate(() => {
        const section = state.data.sections.find(
          (candidate) => candidate.id === id
        );
        if (!section) {
          return;
        }

        const previousDeployUnitId = section.deployUnitId;
        state.data.sections = state.data.sections.filter(
          (candidate) => candidate.id !== id
        );
        resequenceSections(state.data, previousDeployUnitId);

        const siblings = sortSections(
          state.data.sections,
          payload.deployUnitId
        );
        const targetPosition = Math.min(
          Math.max(1, payload.position),
          siblings.length + 1
        );
        siblings.forEach((candidate) => {
          if (candidate.position >= targetPosition) {
            candidate.position += 1;
          }
        });
        section.name = payload.name;
        section.deployUnitId = payload.deployUnitId;
        section.position = targetPosition;
        state.data.sections.push(section);
        resequenceSections(state.data, payload.deployUnitId);
      });
      setNotice("Section updated.");
    },
    deleteSection: (id) => {
      const section = state.data.sections.find(
        (candidate) => candidate.id === id
      );
      if (!section) {
        return false;
      }
      if (state.data.steps.some((step) => step.sectionId === id)) {
        setNotice(
          "Delete all steps in the section before removing it.",
          "error"
        );
        return false;
      }
      mutate(() => {
        state.data.sections = state.data.sections.filter(
          (candidate) => candidate.id !== id
        );
        resequenceSections(state.data, section.deployUnitId);
      });
      setNotice("Section deleted.");
      return true;
    },
    createStep: (payload) => {
      mutate(() => {
        const siblings = sortSteps(state.data.steps, payload.sectionId);
        const targetPosition = Math.min(
          Math.max(1, payload.position || siblings.length + 1),
          siblings.length + 1
        );
        siblings.forEach((step) => {
          if (step.position >= targetPosition) {
            step.position += 1;
          }
        });
        state.data.steps.push({
          id: createId("step"),
          name: payload.name,
          description: payload.description,
          sectionId: payload.sectionId,
          position: targetPosition,
        });
        resequenceSteps(state.data, payload.sectionId);
      });
      setNotice("Step saved.");
    },
    updateStep: (id, payload) => {
      mutate(() => {
        const step = state.data.steps.find((candidate) => candidate.id === id);
        if (!step) {
          return;
        }

        const previousSectionId = step.sectionId;
        state.data.steps = state.data.steps.filter(
          (candidate) => candidate.id !== id
        );
        resequenceSteps(state.data, previousSectionId);

        const siblings = sortSteps(state.data.steps, payload.sectionId);
        const targetPosition = Math.min(
          Math.max(1, payload.position),
          siblings.length + 1
        );
        siblings.forEach((candidate) => {
          if (candidate.position >= targetPosition) {
            candidate.position += 1;
          }
        });
        step.name = payload.name;
        step.description = payload.description;
        step.sectionId = payload.sectionId;
        step.position = targetPosition;
        state.data.steps.push(step);
        resequenceSteps(state.data, payload.sectionId);
      });
      setNotice("Step updated.");
    },
    deleteStep: (id) => {
      mutate(() => {
        const step = state.data.steps.find((candidate) => candidate.id === id);
        state.data.steps = state.data.steps.filter(
          (candidate) => candidate.id !== id
        );
        if (step) {
          resequenceSteps(state.data, step.sectionId);
        }
      });
      setNotice("Step deleted.");
    },
    createRing: (payload) => {
      mutate(() => {
        state.data.rings.push({
          id: createId("ring"),
          createdAt: nowIso(),
          order: state.data.rings.length + 1,
          ...payload,
        });
        resequenceRings(state.data);
      });
      setNotice("Ring saved.");
    },
    updateRing: (id, payload) => {
      mutate(() => {
        const ring = state.data.rings.find((candidate) => candidate.id === id);
        if (!ring) {
          return;
        }
        const targetOrder = Math.min(
          Math.max(1, payload.order),
          state.data.rings.length
        );
        Object.assign(ring, payload, { order: targetOrder });
        resequenceRings(state.data);
      });
      setNotice("Ring updated.");
    },
    deleteRing: (id) => {
      if (state.data.customers.some((customer) => customer.ringId === id)) {
        setNotice(
          "Remove customers from the ring before deleting it.",
          "error"
        );
        return false;
      }
      mutate(() => {
        state.data.rings = state.data.rings.filter((ring) => ring.id !== id);
        resequenceRings(state.data);
      });
      setNotice("Ring deleted.");
      return true;
    },
    moveRing: (id, direction) => {
      mutate(() => {
        const rings = [...state.data.rings].sort(
          (left, right) => left.order - right.order
        );
        const index = rings.findIndex((ring) => ring.id === id);
        const swapIndex = index + direction;
        if (index < 0 || swapIndex < 0 || swapIndex >= rings.length) {
          return;
        }
        const currentOrder = rings[index].order;
        rings[index].order = rings[swapIndex].order;
        rings[swapIndex].order = currentOrder;
        resequenceRings(state.data);
      });
    },
    assignCustomersToRing: (ringId, customerIds) => {
      mutate(() => {
        state.data.customers.forEach((customer) => {
          if (customerIds.includes(customer.id)) {
            customer.ringId = ringId;
          }
        });
      });
      setNotice("Customers assigned to ring.");
    },
    removeCustomerFromRing: (customerId) => {
      mutate(() => {
        const customer = state.data.customers.find(
          (candidate) => candidate.id === customerId
        );
        if (customer) {
          customer.ringId = null;
        }
      });
      setNotice("Customer removed from ring.");
    },
    createCustomer: (payload) => {
      mutate(() => {
        state.data.customers.push({
          id: createId("customer"),
          createdAt: nowIso(),
          ...payload,
        });
      });
      setNotice("Customer saved.");
    },
    updateCustomer: (id, payload) => {
      mutate(() => {
        const customer = state.data.customers.find(
          (candidate) => candidate.id === id
        );
        if (customer) {
          Object.assign(customer, payload);
        }
      });
      setNotice("Customer updated.");
    },
    deleteCustomer: (id) => {
      mutate(() => {
        state.data.customers = state.data.customers.filter(
          (customer) => customer.id !== id
        );
      });
      setNotice("Customer deleted.");
    },
    createNote: (payload) => {
      mutate(() => {
        state.data.futureDeployNotes.push({
          id: createId("note"),
          createdAt: nowIso(),
          ...payload,
        });
      });
      setNotice("Note saved.");
    },
    updateNote: (id, payload) => {
      mutate(() => {
        const note = state.data.futureDeployNotes.find(
          (candidate) => candidate.id === id
        );
        if (note) {
          Object.assign(note, payload);
        }
      });
      setNotice("Note updated.");
    },
    deleteNote: (id) => {
      mutate(() => {
        state.data.futureDeployNotes = state.data.futureDeployNotes.filter(
          (note) => note.id !== id
        );
      });
      setNotice("Note deleted.");
    },
    createPlannedDeploy: (payload) => {
      mutate(() => {
        state.data.plannedDeploys.push({
          id: createId("planned"),
          createdAt: nowIso(),
          updatedAt: nowIso(),
          ...payload,
        });
      });
      setNotice("Planned deploy saved.");
    },
    updatePlannedDeploy: (id, payload) => {
      mutate(() => {
        const plannedDeploy = state.data.plannedDeploys.find(
          (candidate) => candidate.id === id
        );
        if (plannedDeploy) {
          Object.assign(plannedDeploy, payload, { updatedAt: nowIso() });
        }
      });
      setNotice("Planned deploy updated.");
    },
    deletePlannedDeploy: (id) => {
      mutate(() => {
        state.data.plannedDeploys = state.data.plannedDeploys.filter(
          (plannedDeploy) => plannedDeploy.id !== id
        );
      });
      setNotice("Planned deploy deleted.");
    },
    setPlannedDeployStatus: (id, status) => {
      mutate(() => {
        const plannedDeploy = state.data.plannedDeploys.find(
          (candidate) => candidate.id === id
        );
        if (plannedDeploy) {
          plannedDeploy.status = status;
          plannedDeploy.updatedAt = nowIso();
        }
      });
      setNotice("Planned deploy status updated.");
    },
    startPlannedDeploy: (id) => {
      const plannedDeploy = state.data.plannedDeploys.find(
        (candidate) => candidate.id === id
      );
      if (!plannedDeploy || !plannedDeploy.assignedUserId) {
        setNotice("Assign a user before starting a planned deploy.", "error");
        return false;
      }

      mutate(() => {
        plannedDeploy.status = "completed";
        plannedDeploy.updatedAt = nowIso();
        state.ui.plannedHandoff = {
          customerName: plannedDeploy.customerName,
          currentVersion: plannedDeploy.currentVersion,
          targetVersion: plannedDeploy.targetVersion,
          userId: plannedDeploy.assignedUserId,
        };
        state.ui.activeDeploymentId = null;
      });
      setNotice("Planned deploy sent to Deploy with prefilled values.");
      return true;
    },
    createBranch: (payload) => {
      mutate(() => {
        state.data.branches.push({
          id: createId("branch"),
          name: payload.name,
          startingIteration: payload.startingIteration,
          prodIteration: payload.startingIteration,
          devIteration: payload.startingIteration + 1,
          createdAt: nowIso(),
        });
      });
      setNotice("Branch saved.");
    },
    updateBranch: (id, payload) => {
      mutate(() => {
        const branch = state.data.branches.find(
          (candidate) => candidate.id === id
        );
        if (branch) {
          branch.name = payload.name;
          branch.startingIteration = payload.startingIteration;
        }
      });
      setNotice("Branch updated.");
    },
    deleteBranch: (id) => {
      if (
        state.data.deployments.some((deployment) => deployment.branchId === id)
      ) {
        setNotice("Deployments still reference this branch.", "error");
        return false;
      }
      mutate(() => {
        state.data.branches = state.data.branches.filter(
          (branch) => branch.id !== id
        );
      });
      setNotice("Branch deleted.");
      return true;
    },
    createApplication: (name) => {
      mutate(() => {
        state.data.applications.push({
          id: createId("application"),
          name,
          propertyValues: clonePropertyValues(state.data.applicationProperties),
          createdAt: nowIso(),
          updatedAt: nowIso(),
        });
      });
      setNotice("Application saved.");
    },
    renameApplication: (id, name) => {
      mutate(() => {
        const application = state.data.applications.find(
          (candidate) => candidate.id === id
        );
        if (application) {
          application.name = name;
          application.updatedAt = nowIso();
        }
      });
    },
    deleteApplication: (id) => {
      mutate(() => {
        state.data.applications = state.data.applications.filter(
          (application) => application.id !== id
        );
      });
      setNotice("Application deleted.");
    },
    setApplicationValue: (applicationId, propertyId, value) => {
      mutate(() => {
        const application = state.data.applications.find(
          (candidate) => candidate.id === applicationId
        );
        const property = state.data.applicationProperties.find(
          (candidate) => candidate.id === propertyId
        );
        if (application && property) {
          application.propertyValues[propertyId] = normalizeApplicationValue(
            property.type,
            value
          );
          application.updatedAt = nowIso();
        }
      });
    },
    createApplicationProperty: (payload) => {
      mutate(() => {
        const property: ApplicationProperty = {
          id: createId("property"),
          name: payload.name,
          type: payload.type,
          createdAt: nowIso(),
        };
        state.data.applicationProperties.push(property);
        state.data.applications.forEach((application) => {
          application.propertyValues[property.id] = null;
          application.updatedAt = nowIso();
        });
      });
      setNotice("Application property saved.");
    },
    updateApplicationProperty: (id, payload) => {
      mutate(() => {
        const property = state.data.applicationProperties.find(
          (candidate) => candidate.id === id
        );
        if (!property) {
          return;
        }
        property.name = payload.name;
        property.type = payload.type;
        state.data.applications.forEach((application) => {
          application.propertyValues[id] = normalizeApplicationValue(
            payload.type,
            application.propertyValues[id] ?? null
          );
          application.updatedAt = nowIso();
        });
      });
      setNotice("Application property updated.");
    },
    deleteApplicationProperty: (id) => {
      mutate(() => {
        state.data.applicationProperties =
          state.data.applicationProperties.filter(
            (property) => property.id !== id
          );
        state.data.applications.forEach((application) => {
          delete application.propertyValues[id];
          application.updatedAt = nowIso();
        });
      });
      setNotice("Application property deleted.");
    },
    startDeployment: (payload) => {
      const customer = state.data.customers.find(
        (candidate) => candidate.id === payload.customerId
      );
      if (!customer) {
        setNotice(
          "Select a valid customer before starting deployment.",
          "error"
        );
        return false;
      }

      const sectionIds = state.data.sections
        .filter((section) =>
          customer.deployUnitIds.includes(section.deployUnitId)
        )
        .sort((left, right) => left.position - right.position)
        .map((section) => section.id);
      const steps = state.data.steps
        .filter((step) => sectionIds.includes(step.sectionId))
        .sort((left, right) => {
          const leftSection = state.data.sections.find(
            (section) => section.id === left.sectionId
          );
          const rightSection = state.data.sections.find(
            (section) => section.id === right.sectionId
          );
          const leftUnit = leftSection
            ? state.data.deployUnits.find(
                (unit) => unit.id === leftSection.deployUnitId
              )
            : null;
          const rightUnit = rightSection
            ? state.data.deployUnits.find(
                (unit) => unit.id === rightSection.deployUnitId
              )
            : null;
          return (
            (leftUnit?.name ?? "").localeCompare(rightUnit?.name ?? "") ||
            (leftSection?.position ?? 0) - (rightSection?.position ?? 0) ||
            left.position - right.position
          );
        });

      mutate(() => {
        const deployment: Deployment = {
          id: createId("deploy"),
          customerName: customer.name,
          currentVersion: payload.currentVersion,
          targetVersion: payload.targetVersion,
          userId: payload.userId,
          branchId: payload.branchId || null,
          createdAt: nowIso(),
          steps: steps.map((step) => ({
            stepId: step.id,
            completed: false,
            notes: "",
          })),
        };
        state.data.deployments.push(deployment);
        state.ui.activeDeploymentId = deployment.id;
        state.ui.plannedHandoff = null;
      });
      setNotice("Deployment started.");
      return true;
    },
    toggleDeploymentStep: (deploymentId, stepId, completed) => {
      mutate(() => {
        const deployment = state.data.deployments.find(
          (candidate) => candidate.id === deploymentId
        );
        const stepState = deployment?.steps.find(
          (step) => step.stepId === stepId
        );
        if (deployment && stepState) {
          stepState.completed = completed;
          syncCustomerOnCompletedDeployment(deployment);
        }
      });
    },
    updateDeploymentStepNotes: (deploymentId, stepId, notes) => {
      mutate(() => {
        const deployment = state.data.deployments.find(
          (candidate) => candidate.id === deploymentId
        );
        const stepState = deployment?.steps.find(
          (step) => step.stepId === stepId
        );
        if (stepState) {
          stepState.notes = notes;
        }
      });
    },
    deleteAllData: () => {
      mutate(() => {
        state.data = emptyData();
      });
      clearLegacyKeys();
      resetUiState();
      setNotice("All business data deleted.", "info");
    },
    generateDemoData: (size) => {
      mutate(() => {
        state.data = seedData(size);
      });
      clearLegacyKeys();
      resetUiState();
      setNotice(`Demo data generated with ${size} primary records.`);
    },
    exportDeploymentCsv: (deploymentId) => {
      const deployment = state.data.deployments.find(
        (candidate) => candidate.id === deploymentId
      );
      if (!deployment) {
        return;
      }
      const rows = [
        [
          "Deploy ID",
          "Customer",
          "Current Version",
          "Target Version",
          "Deployed By",
          "Deploy Date",
          "Total Steps",
          "Completed Steps",
          "Progress Percent",
          "Step Name",
          "Deploy Unit",
          "Step Completed",
          "Step Notes",
        ],
        ...createDeploymentCsvRows(
          deployment,
          getDeploymentGroups(deployment),
          getUserLabel(deployment.userId)
        ),
      ];
      downloadText(
        `${deployment.customerName.replace(/\s+/g, "-").toLowerCase()}-${
          deployment.id
        }.csv`,
        toCsv(rows),
        "text/csv;charset=utf-8"
      );
    },
    exportFilteredDeploymentsCsv: (deployments) => {
      const rows: string[][] = [
        [
          "Deploy ID",
          "Customer",
          "Current Version",
          "Target Version",
          "Deployed By",
          "Deploy Date",
          "Total Steps",
          "Completed Steps",
          "Progress Percent",
          "Step Name",
          "Deploy Unit",
          "Step Completed",
          "Step Notes",
        ],
      ];
      deployments.forEach((deployment) => {
        rows.push(
          ...createDeploymentCsvRows(
            deployment,
            getDeploymentGroups(deployment),
            getUserLabel(deployment.userId)
          )
        );
      });
      downloadText(
        "deployments-export.csv",
        toCsv(rows),
        "text/csv;charset=utf-8"
      );
    },
    printDeployment: (deploymentId) => {
      const deployment = state.data.deployments.find(
        (candidate) => candidate.id === deploymentId
      );
      if (!deployment) {
        return;
      }
      const progress = getProgress(deployment);
      const groups = getDeploymentGroups(deployment);
      const body = `
        <h1>Deployment summary</h1>
        <div class="meta">
          <div><strong>Customer:</strong> ${deployment.customerName}</div>
          <div><strong>Current Version:</strong> ${
            deployment.currentVersion
          }</div>
          <div><strong>Target Version:</strong> ${
            deployment.targetVersion
          }</div>
          <div><strong>Deploying User:</strong> ${getUserLabel(
            deployment.userId
          )}</div>
          <div><strong>Deploy Date:</strong> ${deployment.createdAt}</div>
          <div><strong>Progress:</strong> ${progress.completed}/${
        progress.total
      } (${progress.percentage}%)</div>
        </div>
        ${
          groups.length
            ? groups
                .map(
                  (unit) => `
                    <div class="card">
                      <h2>${unit.unitName}</h2>
                      ${unit.sections
                        .map(
                          (section) => `
                            <h3>${section.sectionName}</h3>
                            ${section.steps
                              .map(
                                (step) => `
                                  <div class="step">
                                    <div><strong>${step.stepName}</strong> - ${
                                  step.state.completed ? "Completed" : "Pending"
                                }</div>
                                    <div>${step.stepDescription || ""}</div>
                                    <div class="note">${
                                      step.state.notes || ""
                                    }</div>
                                  </div>`
                              )
                              .join("")}
                          `
                        )
                        .join("")}
                    </div>`
                )
                .join("")
            : `<div class="card">This deployment has no applicable steps.</div>`
        }`;
      renderPrintableWindow("Deployment summary", body);
    },
    sendReminder: (message, recipients) => {
      const recipientCount = recipients.filter(Boolean).length;
      setNotice(
        `Reminder sent to ${recipientCount} recipient${
          recipientCount === 1 ? "" : "s"
        }. ${message ? "Message queued in-app." : ""}`
      );
    },
    loadDocs: async () => {
      const response = await fetch(
        `${process.env.BASE_URL}customer-manager-spec.md`
      );
      return response.text();
    },
  };
}
